import { clsx } from 'clsx';
import { useState } from 'react';
import { Text } from '@teachstonellc/component-library';
import { DimensionScoreInput } from './DimensionScoreInput';
import { IndicatorScoreInput } from './IndicatorScoreInput';
import { ChevronRight } from '@teachstonellc/component-library/dist/icons';

import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@radix-ui/react-accordion';

import {
  updateDoubleCoderScores,
  DoubleCoderDimensionScore
} from 'actions/video-obs/video-uploads';

interface DimensionSectionProps {
  videoUploadId: number;
  dimension: DoubleCoderDimensionScore;
}

type DimensionScore = string | null;
type IndicatorScore = { indicator_id: number; score: null | string };

export function DimensionSection({
  videoUploadId,
  dimension
}: DimensionSectionProps) {
  const [dimensionScore, setDimensionScore] = useState<DimensionScore>(
    dimension.dimension_score !== null
      ? String(dimension.dimension_score)
      : null
  );

  const [indicatorScores, setIndicatorScores] = useState<IndicatorScore[]>(
    dimension.indicators.map(indicator => ({
      indicator_id: indicator.id,
      score: indicator.score !== null ? String(indicator.score) : null
    }))
  );

  function allScoresEntered(
    dimScore: DimensionScore,
    indScores: IndicatorScore[]
  ) {
    if (!dimScore) {
      return false;
    }

    for (const indicator of dimension.indicators) {
      const scoreObj = indScores.find(s => s.indicator_id === indicator.id);

      if (!scoreObj || scoreObj.score === null) {
        return false;
      }
    }

    return true;
  }

  function handleDimensionScoreChange(score: string) {
    setDimensionScore(() => {
      if (allScoresEntered(score, indicatorScores)) {
        submitScores(score, indicatorScores);
      }

      return score;
    });
  }

  function handleIndicatorScoreChange(indicatorId: number, score: string) {
    setIndicatorScores(prevScores => {
      const newScores = [
        ...prevScores.filter(s => s.indicator_id !== indicatorId),
        { indicator_id: indicatorId, score }
      ];

      if (allScoresEntered(dimensionScore, newScores)) {
        submitScores(dimensionScore, newScores);
      }

      return newScores;
    });
  }

  function submitScores(dimScore: DimensionScore, indScores: IndicatorScore[]) {
    const params = {
      double_coder_score: {
        dimension_id: dimension.id,
        dimension_score: parseInt(dimScore!),
        indicator_scores: indScores.map(s => ({
          indicator_id: s.indicator_id,
          score: parseInt(s.score!)
        }))
      }
    };

    updateDoubleCoderScores(videoUploadId, params);
  }

  function getIndicatorScore(indicatorId: number) {
    const scoreObj = indicatorScores.find(s => s.indicator_id === indicatorId);
    return scoreObj ? scoreObj.score : null;
  }

  function formatIndicatorScore(score: string | null) {
    switch (score) {
      case '0':
        return 'L';
      case '50':
        return 'M';
      case '100':
        return 'H';
      default:
        return '-';
    }
  }

  return (
    <AccordionItem
      value={dimension.slug}
      className="border-b border-ds-border pt-4 first:pt-0 pb-4 last:border-0 last:pb-0"
    >
      <AccordionTrigger className="group">
        <div className="hover:bg-ds-black-alpha-1 p-2 rounded-md hover:cursor-pointer -mx-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div
                className={clsx(
                  'rounded-md w-5 h-5 flex items-center justify-center',
                  dimensionScore
                    ? 'bg-ds-blue-9 text-ds-white'
                    : 'bg-ds-black-alpha-2'
                )}
              >
                <Text variant="h5" as="label">
                  {dimensionScore || '-'}
                </Text>
              </div>

              <Text variant="h4" as="label">
                {dimension.name}
              </Text>
            </div>

            <ChevronRight
              size={24}
              className="text-ds-icon-subtle group-data-[state=open]:rotate-90"
            />
          </div>

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
            {dimension.indicators.map(indicator => {
              const fScore = formatIndicatorScore(
                getIndicatorScore(indicator.id)
              );

              return (
                <div key={indicator.slug} className="flex items-center gap-2">
                  <Text variant="label1" className="text-ds-text-subtle">
                    {indicator.name}
                  </Text>

                  <div className="flex items-center justify-center rounded-md w-5 h-5 bg-ds-fuschia-3">
                    <Text variant="footnote">{fScore}</Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AccordionTrigger>

      <AccordionContent className="mt-2 space-y-3">
        <div className="flex flex-col gap-1">
          <Text variant="label0">Dimension Score</Text>
          <DimensionScoreInput
            value={dimensionScore}
            onChange={handleDimensionScoreChange}
          />
        </div>

        {dimension.indicators.map(indicator => (
          <div key={indicator.slug} className="flex flex-col gap-0.5">
            <Text variant="label0">{indicator.name}</Text>
            <IndicatorScoreInput
              value={getIndicatorScore(indicator.id)}
              onChange={score =>
                handleIndicatorScoreChange(indicator.id, score)
              }
            />
          </div>
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
